"use client";

import React, { useEffect, useState, useRef, type RefObject } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref: ref as RefObject<T>,
    isVisible
  };
}
export default function VanguardiaEnNegroYAmarillo() {
  const [scrollY, setScrollY] = useState(0);
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="226436" id="miles-henshaw-sound-vision" className="bg-background text-foreground relative min-h-[100svh] w-full overflow-hidden flex items-center pt-24 pb-16 lg:py-0 !bg-[url(https://possible-crane.10web.cloud/wp-content/uploads/2026/04/8808CBAD-16F5-4C54-A5CC-AE41BE9A5E10-scaled.jpeg)] bg-center">
    <div className="absolute inset-0 bg-gradient-to-br from-background via-background/90 to-black z-0 pointer-events-none opacity-80" />

    <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 h-full">
      <div className="flex flex-col lg:flex-row items-center h-full">
        
        <div ref={textRef} className={`w-full lg:w-1/2 flex flex-col justify-center z-20 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <h1 className="text-6xl sm:text-7xl md:text-8xl lg:text-[7rem] xl:text-[8rem] font-bold font-default uppercase leading-[0.85] tracking-tighter text-white">
            Sound &
            <br />
            Vision
            <br />
            <span className="text-primary block mt-2">Miles Henshaw</span>
          </h1>
          
          <p className="mt-8 text-lg sm:text-xl text-muted-foreground max-w-md font-sans">Music producer and mix engineer refining the raw energy of alternative and experimental Music.</p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Button asChild size="lg" className="h-14 px-8 text-base font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="https://iammileshen.com/the-sound/">
                Explore Work
                <ArrowRight className="ml-2 size-5" />
              </Link>
            </Button>
            <Button asChild variant="ghost" size="lg" className="h-14 px-8 text-base font-semibold rounded-lg border border-white/20 hover:bg-white/5 hover:text-white text-white">
              <Link to="https://iammileshen.com/get-in-touch/">
                Get In Touch
              </Link>
            </Button>
          </div>
        </div>

        <div className="w-full lg:w-1/2 h-[50vh] lg:h-screen relative mt-16 lg:mt-0 pointer-events-none flex items-center justify-center lg:justify-end" />

      </div>
    </div>
  </section>;
}